import React, { createContext, useContext, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectCurrentUser } from '../../slices/userSlice';
import {
  fetchUserConversations,
  selectConversations,
  selectMessagesLoading
} from '../../slices/messageSlice';
import {
  setUserId,
  selectIsConnected
} from '../../slices/sseSlice';
import securityService from './services/SecurityService';
import {
  MESSAGE_TYPE_CONFIG
} from '../../constants/messageConstants';

/**
 * Message Context for global messaging state
 */
const MessageContext = createContext({
  // User context
  currentUser: null,
  userRole: null,

  // Messaging state
  conversations: [],
  isLoading: false,
  isConnected: false,

  // Business messaging
  businessMessageTypes: [],
  unreadCounts: { total: 0, business: 0 },

  // Actions
  refreshConversations: () => { },
  setUserRole: () => { },

  // Security context
  securityProfile: null,
  canSendMessage: () => true,
  canReceiveMessage: () => true
});

/**
 * Business Message Configuration
 * Extended configuration for additional message types not in MESSAGE_TYPE_CONFIG
 */
const BUSINESS_MESSAGE_CONFIG = {
  ...MESSAGE_TYPE_CONFIG,
  // Add any application-specific overrides or additional message types here
  'SYSTEM_NOTIFICATION': {
    name: 'System Notification',
    description: 'System-wide announcement',
    priority: 'LOW',
    targetRoles: ['ALL'],
    senderRoles: ['ADMIN', 'SYSTEM'],
    icon: 'info circle',
    color: '#767676',
    requiresAction: false,
    actions: ['DISMISS'],
    displayName: 'System Notification'
  },
  'HELP_DESK': {
    name: 'Help Desk',
    description: 'Support request or help message',
    priority: 'MEDIUM',
    targetRoles: ['ADMIN', 'SUPPORT'],
    senderRoles: ['GUEST', 'VOICE_ACTOR', 'BOOK_CREATOR'],
    icon: 'help circle',
    color: '#6435c9',
    requiresAction: true,
    actions: ['RESPOND', 'ESCALATE', 'RESOLVE'],
    displayName: 'Help Desk'
  }
};

/**
 * MessageProvider Component
 * Provides global messaging context to the application
 */
export const MessageProvider = ({ children }) => {
  const dispatch = useDispatch();
  const currentUser = useSelector(selectCurrentUser);
  const conversations = useSelector(selectConversations);
  const isLoading = useSelector(selectMessagesLoading);
  const isConnected = useSelector(selectIsConnected);

  // Local state
  const [userRole, setUserRole] = useState(null);
  const [securityProfile, setSecurityProfile] = useState(null);
  const [businessMessageTypes, setBusinessMessageTypes] = useState([]);
  const [unreadCounts, setUnreadCounts] = useState({ total: 0, business: 0 });
  const [securityInitialized, setSecurityInitialized] = useState(false);
  const [securityStatus, setSecurityStatus] = useState({ securityLevel: 'DEGRADED' });

  // Initialize SecurityService with proper authentication timing
  useEffect(() => {
    const initializeSecurity = async () => {
      try {
        // Check if user has valid authentication token before initializing
        if (!securityService.hasValidToken()) {
          console.log('MessageProvider: No valid token found, SecurityService will use graceful degradation');
          setSecurityInitialized(false);
          setSecurityStatus({ securityLevel: 'DEGRADED', error: 'No authentication token available' });
          return;
        }

        // Only initialize if we have a valid token
        const result = await securityService.initialize();

        if (result.success) {
          setSecurityInitialized(true);
          setSecurityStatus(securityService.getSecurityStatus());
          console.log('MessageProvider: SecurityService initialized successfully');
        } else {
          console.warn('MessageProvider: SecurityService initialization failed, using graceful degradation');
          setSecurityInitialized(false);
          setSecurityStatus({ securityLevel: 'DEGRADED', error: 'Failed to load security definitions' });
        }
      } catch (error) {
        console.error('MessageProvider: Failed to initialize SecurityService:', error);
        setSecurityInitialized(false);
        setSecurityStatus({ securityLevel: 'DEGRADED', error: error.message });
      }
    };

    // Delay initialization slightly to ensure user authentication is complete
    const timeoutId = setTimeout(initializeSecurity, 100);

    return () => clearTimeout(timeoutId);
  }, []);

  // Initialize user context
  useEffect(() => {
    if (currentUser?.userId) {
      dispatch(setUserId(currentUser.userId));

      // Determine user role from user data
      const role = currentUser.primaryRole || currentUser.role || 'user';
      setUserRole(role.toLowerCase());

      // Set enhanced security profile with SecurityService integration
      setSecurityProfile({
        userId: currentUser.userId,
        roles: currentUser.roles || [role],
        primaryRole: role,
        permissions: currentUser.permissions || [],
        authorized: true,
        securityLevel: securityStatus.securityLevel || 'MEDIUM',
        securityInitialized: securityInitialized
      });

      // Fetch initial conversations
      dispatch(fetchUserConversations(currentUser.userId));
    } else {
      // Handle guest users
      setUserRole('guest');
      setSecurityProfile({
        userId: null,
        roles: ['guest'],
        primaryRole: 'guest',
        permissions: ['send_help_desk'],
        authorized: false,
        securityLevel: 'LOW',
        securityInitialized: securityInitialized
      });
    }
  }, [currentUser, dispatch, securityInitialized, securityStatus]);

  // Calculate unread counts and business message types
  useEffect(() => {
    if (!conversations || conversations.length === 0) {
      setUnreadCounts({ total: 0, business: 0 });
      setBusinessMessageTypes([]);
      return;
    }

    const allMessages = conversations.flatMap(conv => conv.messages || []);
    const unreadMessages = allMessages.filter(msg =>
      !msg.isRead && msg.recipientId === currentUser?.userId
    );

    const businessMessages = unreadMessages.filter(msg =>
      msg.messageType && BUSINESS_MESSAGE_CONFIG[msg.messageType]
    );

    const businessTypes = [...new Set(businessMessages.map(msg => msg.messageType))];

    setUnreadCounts({
      total: unreadMessages.length,
      business: businessMessages.length
    });

    setBusinessMessageTypes(businessTypes);
  }, [conversations, currentUser?.userId]);

  // Refresh conversations
  const refreshConversations = () => {
    if (currentUser?.userId) {
      dispatch(fetchUserConversations(currentUser.userId));
    }
  };

  // Enhanced security validation with SecurityService integration
  const canSendMessage = async (messageType = null, recipientContext = null) => {
    try {
      // Fallback to basic validation if SecurityService not initialized
      if (!securityInitialized) {
        if (!securityProfile?.authorized && userRole !== 'guest') {
          return false;
        }

        if (!messageType) return true;

        const config = BUSINESS_MESSAGE_CONFIG[messageType];
        if (!config) return true;

        const userRoleUpper = userRole?.toUpperCase() || 'GUEST';
        return config.senderRoles.includes(userRoleUpper) || userRole === 'admin';
      }

      // Use comprehensive SecurityService validation
      const result = await securityService.canSendMessage(messageType, securityProfile, recipientContext);

      // Log security audit for high-security messages
      if (result.securityLevel === 'CRITICAL' || result.securityLevel === 'HIGH') {
        await securityService.logSecurityAudit('MESSAGE_SEND_CHECK', {
          messageType,
          userId: securityProfile?.userId,
          userRole: userRole,
          allowed: result.allowed,
          restrictions: result.restrictions,
          securityLevel: result.securityLevel
        });
      }

      return result.allowed;
    } catch (error) {
      console.error('Error in canSendMessage:', error);
      // Fallback to restrictive policy on error
      return false;
    }
  };

  // Enhanced receive message validation with SecurityService integration
  const canReceiveMessage = async (messageType = null) => {
    try {
      // Fallback to basic validation if SecurityService not initialized
      if (!securityInitialized) {
        if (!messageType) return true;

        const config = BUSINESS_MESSAGE_CONFIG[messageType];
        if (!config) return true;

        const userRoleUpper = userRole?.toUpperCase() || 'GUEST';
        return config.targetRoles.includes('ALL') ||
          config.targetRoles.includes(userRoleUpper) ||
          userRole === 'admin';
      }

      // Use comprehensive SecurityService validation
      const result = await securityService.canReceiveMessage(messageType, securityProfile);

      return result.allowed;
    } catch (error) {
      console.error('Error in canReceiveMessage:', error);
      // Fallback to restrictive policy on error
      return false;
    }
  };

  // Validate complete message security
  const validateMessageSecurity = (messageData) => {
    if (!securityInitialized) {
      return {
        valid: true,
        warnings: ['Security service not initialized - using basic validation'],
        securityLevel: 'MEDIUM'
      };
    }

    return securityService.validateMessageSecurity(messageData, securityProfile);
  };

  // Get business message configuration
  const getBusinessMessageConfig = (messageType) => {
    return BUSINESS_MESSAGE_CONFIG[messageType] || null;
  };

  // Get all business message configurations for current user
  const getAvailableMessageTypes = () => {
    if (!userRole) return [];

    return Object.entries(BUSINESS_MESSAGE_CONFIG)
      .filter(([messageType]) => canSendMessage(messageType))
      .map(([messageType, config]) => ({
        type: messageType,
        ...config
      }));
  };

  // Context value
  const contextValue = {
    // User context
    currentUser,
    userRole,

    // Messaging state
    conversations,
    isLoading: isLoading.conversations || isLoading.messages,
    isConnected,

    // Business messaging
    businessMessageTypes,
    unreadCounts,
    businessMessageConfig: BUSINESS_MESSAGE_CONFIG,

    // Actions
    refreshConversations,
    setUserRole,

    // Security context (Enhanced with SecurityRegistryManager integration)
    securityProfile,
    securityInitialized,
    securityStatus,
    canSendMessage,
    canReceiveMessage,
    validateMessageSecurity,
    getBusinessMessageConfig,
    getAvailableMessageTypes,

    // Security service access
    securityService: securityInitialized ? securityService : null,

    // Utility functions
    isBusinessMessage: (messageType) => !!BUSINESS_MESSAGE_CONFIG[messageType],
    getMessagePriority: (messageType) => {
      const config = BUSINESS_MESSAGE_CONFIG[messageType];
      if (!config) return 0;

      const priorities = { 'CRITICAL': 4, 'HIGH': 3, 'MEDIUM': 2, 'LOW': 1 };
      return priorities[config.priority] || 0;
    },

    // Enhanced security utilities
    getSecurityLevel: () => securityStatus.securityLevel || 'DEGRADED',
    isSecurityActive: () => securityInitialized && securityStatus.securityLevel === 'ACTIVE',
    refreshSecurityDefinitions: async () => {
      if (securityInitialized) {
        const result = await securityService.refreshSecurityDefinitions();
        setSecurityStatus(securityService.getSecurityStatus());
        return result;
      }
      return { success: false, error: 'Security service not initialized' };
    }
  };

  return (
    <MessageContext.Provider value={contextValue}>
      {children}
    </MessageContext.Provider>
  );
};

/**
 * Custom hook to use the Message Context
 */
export const useMessageContext = () => {
  const context = useContext(MessageContext);

  if (!context) {
    throw new Error('useMessageContext must be used within a MessageProvider');
  }

  return context;
};

/**
 * HOC to wrap components with MessageProvider
 */
export const withMessageProvider = (Component) => {
  return function WrappedComponent(props) {
    return (
      <MessageProvider>
        <Component {...props} />
      </MessageProvider>
    );
  };
};

export default MessageProvider;
